import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { Icon, Dropdown } from '@column/column-ui-kit';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { ROUTE } from '~/app/routes';
import { useSessionStore } from '~/stores/Session';

const Panel = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const Avatar = styled.div`
  display: block;
  width: 36px;
  height: 36px;
  line-height: 36px;
  border-radius: 5px;
  font-weight: 600;
  font-size: 12px;
  text-align: center;
  text-transform: uppercase;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  background-color: ${({ theme }) => theme.secondary.blendToBackground(125)};

  ${({ theme }) =>
    theme.id !== 'default' &&
    css`
      color: ${() => theme.secondary.blendToBackground(600)};
      background-color: ${() => theme.secondary.blendToBackground(25)};
    `}
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Name = styled.div`
  font-size: 14px;
  line-height: 24px;
  align-items: center;
  color: ${({ theme }) => theme.secondary.background};
`;

const Arrow = styled(Icon.ChevronDown)`
  --icon-color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  --icon-size: 12px;
`;

const StyledDropdown = styled(Dropdown)`
  --dropdown-custom-padding: 0;
`;

export const User: React.FC = () => {
  const { settings, changeTheme, currentUser } = useSessionStore();
  const navigate = useNavigate();
  const [userAnimate, setUserAnimate] = useState<boolean>(false);
  const [modeAnimate, setModeAnimate] = useState<boolean>(false);
  const [logoutAnimate, setLogoutAnimate] = useState<boolean>(false);

  const handleLogout = useCallback(() => {
    navigate(PUBLIC_ROUTE.LOGIN, {
      state: {
        action: 'signOut',
      },
    });
  }, []);

  return (
    <StyledDropdown
      customLabel={
        <Panel>
          <Avatar>
            {currentUser?.firstName.charAt(0)}
            {currentUser?.lastName.charAt(0)}
          </Avatar>
          <Label>
            <Name>{currentUser?.firstName}</Name>
            <Arrow />
          </Label>
        </Panel>
      }
      positionRight
      options={[
        {
          label: 'Profile',
          icon: <Icon.AnimationUser running={userAnimate} onDone={() => setUserAnimate(false)} />,
          onClick: () => navigate(ROUTE.PROFILE),
          onHover: (value: boolean) => value && !userAnimate && setUserAnimate(true),
        },
        {
          label: settings.themeType === 'default' ? 'Lights off' : 'Lights on',
          icon: <Icon.AnimationMode running={modeAnimate} onDone={() => setModeAnimate(false)} />,
          onClick: () => changeTheme(),
          onHover: (value: boolean) => value && !modeAnimate && setModeAnimate(true),
        },
        {
          label: 'Logout',
          icon: <Icon.AnimationLogout running={logoutAnimate} onDone={() => setLogoutAnimate(false)} />,
          onClick: () => handleLogout(),
          onHover: (value: boolean) => value && !logoutAnimate && setLogoutAnimate(true),
        },
      ]}
    />
  );
};
